import { __, filter, flatten, gt, isNil, length, pipe, pluck, propEq, prop, props, reject, sum, map, groupBy } from 'ramda';
import productTypes from 'constants/productTypes';
import {
  PRIMARY_LOYALTY_ACCOUNT_ADDITIONAL_DETAILS_KEY,
  HOTEL_STATUSES,
  OUTBOUND,
  INBOUND,
  MULTI_JOURNEY_KEY,
  ANY_BELGIAN_STATION_UIC,
  ADS_THROUGH_BRUSSELS_UIC,
  NEW_FARE_CONDITION_DATE_ON_EXCHANGE,
  GDS
} from 'constants/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { marginTariff, EVAC_ASSIST_PRODUCT_CODE } from 'constants/tariffCodes';
import { BOOKING_TYPES, bookingStatus, NOT_THIRD_PARTY_BOOKING } from 'types/booking';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { codes, statusShortCode } from 'constants/payment';
import { getPassengersWithExchangeInTicketChain } from './isReAccommodation';
import {
  isUserEurostarAgent,
  isUserStationAgent,
  isUserThalysAgent,
  isUserInEBSCOffice,
  isUserInCSCGroup,
  isTravelAgent
} from 'utils/user';
import { OVERPAID_THRESHOLD_AMOUNT } from '../types/constants';
import config from 'utils/config';
import moment from 'moment';
import { ARIA_AGENTS } from '../constants/agents';
import { types as passengerTypes } from 'types/passengers';
import { B, TB, H, TH } from 'constants/inventoryClass';
import { getOldClassOfService } from './classOfService';
import { BP, PREMIUM, NCO_DATE, SEVEN_DAY_EXCHANGE_RULE_CLASSES_OF_SERVICE } from 'constants/classOfService';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';

const PAYOUT = 'payout';
const BALANCE = 'balance';

const { ON_HOLD, PROVISIONAL, TEMPORARILY_ON_HOLD, CONFIRMED, OVERPAID, IN_AFTERSALES, VOIDED, EXPIRED_ON_HOLD, CANCELLED, REFUNDED } =
  bookingStatus;

export const getVoidedProduct = ({ status, outbound = [], inbound = [] }) => {
  const outboundProducts = outbound?.flatMap(({ products }) => products);
  const inboundProducts = inbound?.flatMap(({ products }) => products);
  const allProducts = [...outboundProducts, ...inboundProducts];
  return status === CONFIRMED
    ? allProducts?.find(
        ({ cancelled, provisional, voidTimestamp }) => !cancelled && provisional && !!voidTimestamp && moment(voidTimestamp).isValid()
      ) ?? null
    : null;
};

export const getDisplayStatus = ({
  status,
  isInAfterSales,
  totalPriceToBePaid,
  outbound = [],
  inbound = [],
  latestRevisionSource,
  cancelledTimestamp
}) => {
  if (getVoidedProduct({ status, outbound, inbound })) return VOIDED;
  if (isOverPaid(status, totalPriceToBePaid)) return OVERPAID;
  if (isExpiredOnHold(status, cancelledTimestamp, latestRevisionSource)) return EXPIRED_ON_HOLD;
  return isInAfterSales ? IN_AFTERSALES : status;
};

export const isPublicBPPremiumNCO = ({ outbound, inbound, requestedProductFamilies }) => {
  const { outboundOriginalDate, outboundOriginalClassOfService, outboundDate } = outbound;
  const { inboundOriginalDate, inboundOriginalClassOfService, inboundDate } = inbound;

  const isBeforeNCO = (date) => moment(date).isSameOrBefore(moment(NCO_DATE));
  const isAfterNCO = (date) => moment(date).isSameOrAfter(moment(NCO_DATE));
  const isBPorPremium = (classOfService) => [BP, PREMIUM].includes(classOfService);

  if (
    (isBeforeNCO(outboundOriginalDate) || isBeforeNCO(inboundOriginalDate)) &&
    (isBPorPremium(outboundOriginalClassOfService) || isBPorPremium(inboundOriginalClassOfService)) &&
    (isAfterNCO(outboundDate) || isAfterNCO(inboundDate)) &&
    requestedProductFamilies[0] === productFamilyCodes.PUB
  ) {
    return true;
  } else return false;
};

export const showCancelThirdPartyBookingWarning = ({ booking, user, selectedItemRefs = [] }) => {
  const bookingCreatedByThirdParty = !NOT_THIRD_PARTY_BOOKING.includes(booking.salesChannelCode);
  const items = [...booking.outbound, ...booking.inbound];
  const nonCancelledItems = items?.map((t) => t.products?.filter(({ cancelled }) => !cancelled)?.flat())?.flat();

  const bookingItems =
    selectedItemRefs.length > 0
      ? selectedItemRefs?.map((ref) => nonCancelledItems?.filter(({ itemRef }) => itemRef === ref))?.flat()
      : nonCancelledItems;
  const totalPrice = sum(bookingItems.map(({ price }) => price));
  const ticketsToCancelHavePrice = totalPrice > 0;

  return !isTravelAgent(user) && bookingCreatedByThirdParty && ticketsToCancelHavePrice;
};

/**
 * Checks a booking to see if it includes a non cancelled extra product of the given type.
 *
 * @param {Object} booking
 * @param {String} productType
 */
export const includesExtraProductOfType = (booking, productType) =>
  pipe(
    props(['outbound', 'inbound']),
    flatten,
    reject(isNil),
    pluck('extraProducts'),
    flatten,
    filter(propEq('type', productType)),
    reject(propEq('cancelled', true)),
    length,
    gt(__, 0)
  )(booking);

const buildCustomerFromBillingDetails = ({
  firstName,
  lastName,
  addressLine1,
  addressLine2,
  addressLine3,
  city,
  postalCode,
  countryCode,
  email,
  phoneCountryCode,
  phoneNumber
}) => ({
  firstName,
  lastName,
  email,
  phoneNumber: `${parseInt(phoneCountryCode).toString().padStart(3, '0')}${phoneNumber}`,
  address: {
    countryCode,
    city,
    postalCode,
    street: [addressLine1, addressLine2, addressLine3]
      .map((addressLine) => addressLine.trim())
      .filter((addressLine) => addressLine)
      .join(', ')
  }
});

const hasPayoutOrBalance = (key) => (refId, payments) =>
  payments.some(({ reference }) => reference && reference.includes(refId) && reference.toLowerCase().includes(key));

const hasPayout = hasPayoutOrBalance(PAYOUT);

const checkPaymentType = (key) => (reference) => reference && reference.includes(key);

const isPayout = checkPaymentType(PAYOUT);
const isBalance = checkPaymentType(BALANCE);

const convertGDSPayments = (payments) =>
  payments.map((p) => ({
    ...p,
    reference: p.reference ? p.reference.replace('BALANCE', BALANCE).replace('PAYOUT', PAYOUT) : p.reference
  }));
const isOverPaid = (status, totalPriceToBePaid) => status === CONFIRMED && totalPriceToBePaid < OVERPAID_THRESHOLD_AMOUNT;

const isExpiredOnHold = (status, cancelledTimestamp, latestRevisionSource) => {
  const isTheLastElementsUsernameCleanUp = latestRevisionSource?.username === 'CLEAN_UP';
  return (status === CANCELLED || status === REFUNDED) && cancelledTimestamp && isTheLastElementsUsernameCleanUp;
};

const hasParentPayment = (reference, payments) => payments.some(({ refID }) => reference.includes(refID));

const getAssistances = (extras = []) => extras.filter(({ cancelled, type }) => type === productTypes.DISABILITY_SUPPORT && !cancelled);

const getEvacAssistItemRefs = (extras = []) => extras.filter(({ code }) => code === EVAC_ASSIST_PRODUCT_CODE).map(({ itemRef }) => itemRef);

const invalidMarginTariffCodes = [marginTariff.MARGIN_TRAIN, marginTariff.MARGIN_TRAIN_ER, marginTariff.MARGIN_HOTEL];

const getValidExtras = (extras = []) =>
  extras.filter(
    ({ cancelled, type, tariffDetails: { tariffCode = '' } = {} }) =>
      ![
        productTypes.PENALTY_FARE,
        productTypes.FEE,
        productTypes.DISABILITY_SUPPORT,
        productTypes.TICKET_WITH_SEAT,
        productTypes.SEAT_RESERVATION_ONLY,
        productTypes.TICKET_WITH_NO_SEAT,
        productTypes.VOUCHER
      ].includes(type) &&
      !invalidMarginTariffCodes.includes(tariffCode) &&
      !cancelled
  );

const findPayoutAndBalanceByRefId = (refId, payments) =>
  payments.filter(
    ({ reference }) => reference && reference.includes(refId) && [PAYOUT, BALANCE].find((key) => reference.toLowerCase().includes(key))
  );

const hasHotel = (booking) => !!(booking?.hotels && booking?.hotels?.length);

const getBookingAttachedLoyaltyAccount = (booking) =>
  booking?.additionalDetails?.find((d) => d.key === PRIMARY_LOYALTY_ACCOUNT_ADDITIONAL_DETAILS_KEY)?.value;

const buildBookingDataToSaveToCustomer = (booking) => {
  const directions = ['outbound'];
  if (booking.inbound.length > 0) {
    directions.push('inbound');
  }

  return {
    reference: booking.reference,
    date: booking.outbound[0].timing.isoDate,
    tickets: directions.map((direction) => {
      const firstLeg = booking[direction][0];
      const lastLeg = booking[direction][booking[direction].length - 1];

      return {
        serviceClass: firstLeg.products.find((p) => p.inventoryClass)?.inventoryClass,
        departureTime: firstLeg.timing.isoDate,
        direct: booking[direction].length === 1,
        origin: {
          id: firstLeg.origin.uic,
          name: firstLeg.origin.name,
          country: firstLeg.origin.countryCode
        },
        destination: {
          id: lastLeg.destination.uic,
          name: lastLeg.destination.name,
          country: lastLeg.destination.countryCode
        },
        passengers: booking.passengers.map((p) => ({
          firstName: p.firstName,
          lastName: p.lastName
        }))
      };
    })
  };
};

const mapJourneyForPointsRedemption = (legs) => {
  const eurostarLeg = legs.find((leg) => leg.serviceType.code === serviceTypeCodes.EUROSTAR);

  return {
    classOfAccommodation: getOldClassOfService(eurostarLeg.classOfService.code),
    classOfService: eurostarLeg.classOfService.code,
    bucketCodes: eurostarLeg.products.map((p) => p.tariffDetails.bucketCode)
  };
};

const buildPointsRedemptionData = ({ booking, accountId }) => ({
  accountId,
  bookingReference: booking.reference,
  origin: booking.outbound[0].origin.uic,
  destination: booking.outbound[booking.outbound.length - 1].destination.uic,
  quantity: booking.passengers.length,
  outbound: mapJourneyForPointsRedemption(booking.outbound),
  departureDateTime: booking.outbound[0].timing.isoDate,
  ...(booking.inbound &&
    booking.inbound.length && {
      inbound: mapJourneyForPointsRedemption(booking.inbound),
      returnDateTime: booking.inbound[0].timing.isoDate
    })
});

const buildPointsRedemptionLoyaltyData = ({ booking, membershipNumber, useNewLoyaltySystem, actionBy }) => {
  const outboundLeg = booking.outbound?.find((leg) => Object.values(serviceTypeCodes).includes(leg.serviceType.code));
  const inboundLeg = booking.inbound?.find((leg) => Object.values(serviceTypeCodes).includes(leg.serviceType.code));

  return {
    actionBy,
    membershipNumber,
    useNewLoyaltySystem: useNewLoyaltySystem,
    bookingReference: booking.reference,
    origin: booking.outbound[0].origin.uic,
    destination: booking.outbound[booking.outbound.length - 1].destination.uic,
    quantity: booking.passengers.length,
    outbound: {
      classOfAccommodation: getOldClassOfService(outboundLeg.classOfService.code),
      classOfService: outboundLeg.classOfService.code,
      bucketCodes: outboundLeg.products.map((p) => p.tariffDetails.bucketCode),
      departureDateTime: booking.outbound[0].timing.isoDate
    },
    ...(inboundLeg && {
      inbound: {
        classOfAccommodation: getOldClassOfService(outboundLeg.classOfService.code),
        classOfService: inboundLeg.classOfService.code,
        bucketCodes: inboundLeg.products.map((p) => p.tariffDetails.bucketCode),
        departureDateTime: booking.inbound[0].timing.isoDate
      }
    })
  };
};

const isOldBookingConfirmedTimestamp = (confirmedTimestamp) => {
  const adjustedPointsDate = config('ADJUSTED_POINTS_DATE', { canOverride: true });

  return new Date(confirmedTimestamp) < new Date(adjustedPointsDate ?? 'Sun Oct 01 2023');
};

const isLoyaltyBooking = (booking) => {
  const hasLoyaltyProdFamilies = booking.productFamilies?.some((pf) => pf.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY);

  const hasLoyaltyPayments = booking.payments?.some((p) => p.method === codes.LOYALTY_POINTS && p.paymentStatus === 'S');

  return hasLoyaltyProdFamilies || hasLoyaltyPayments;
};

const loyaltyPointsDifferencesCreationAndCancelLegs = ({ pendingCreationLegs, pendingCancellationLegs } = {}) => {
  const getPendingCreationBookingItems = pendingCreationLegs ? flatten(pendingCreationLegs.map((pl) => pl.bookingItems)) : [];
  const getPendingCancellationBookingItems = pendingCancellationLegs ? flatten(pendingCancellationLegs.map((pl) => pl.bookingItems)) : [];

  const pendingCancellationPoints = sum(map(prop('loyaltyPoints'), getPendingCancellationBookingItems));
  const pendingCreationPoints = sum(map(prop('loyaltyPoints'), getPendingCreationBookingItems));

  return pendingCreationPoints - pendingCancellationPoints;
};

const calculateMilesUsed = (booking = {}) => {
  let milesUsed = 0;

  if (booking.productFamily !== productFamilyCodes.ER_STAFF || !hasBookingBeenCreatedInSameQuarter(booking)) {
    return milesUsed;
  }

  const { inbound, outbound } = booking;

  const allLegs = (outbound || []).concat(inbound || []);

  allLegs.forEach((leg) => {
    leg.bookingItems
      .filter((bookingItem) => !bookingItem.cancelled && !bookingItem.provisional)
      .forEach((bookingItem) => {
        milesUsed += bookingItem.inventoryClass === TB ? 1 : 2;
      });
  });

  return milesUsed;
};

const getQuarter = (date) => {
  const dateInMoment = moment(date);

  if (!date || !dateInMoment.isValid()) {
    return 0;
  }

  const month = dateInMoment.month();
  const quarter = Math.floor((month / 12) * 4) + 1;

  return quarter;
};

const hasBookingBeenCreatedInSameQuarter = (booking) => {
  const bookingQuarter = getQuarter(booking.createTimestamp);
  const currentQuarter = getQuarter(new Date(Date.now()).toISOString().split('T').shift());

  return bookingQuarter > 0 && currentQuarter > 0 && bookingQuarter === currentQuarter;
};

const getHotelsStatuses = (hotels = [], jacTravelHotels, isDotwBooking = false) =>
  hotels
    .filter(({ cancelled }) => (hotels.length > 1 ? !cancelled : true))
    ?.map((hotel) => {
      const hotelInfo = {
        reference: hotel.jacTravelRef,
        s3Status: hotel.cancelled ? HOTEL_STATUSES.CANCELLED : HOTEL_STATUSES.NOT_CANCELLED
      };

      const jacTravelHotel = isDotwBooking ? jacTravelHotels : jacTravelHotels.find((h) => h.bookingReference === hotel.jacTravelRef);

      if (!jacTravelHotel) {
        hotelInfo.jacTravelStatus = HOTEL_STATUSES.NOT_FOUND;
      } else {
        hotelInfo.jacTravelStatus = isDotwBooking
          ? jacTravelHotel?.rooms?.[0]?.status === 'CANCELED'
            ? HOTEL_STATUSES.CANCELLED
            : HOTEL_STATUSES.NOT_CANCELLED
          : jacTravelHotel.status === 'Cancelled'
          ? HOTEL_STATUSES.CANCELLED
          : HOTEL_STATUSES.NOT_CANCELLED;
      }

      return hotelInfo;
    });

const findJacTravelHotel = (jacTravelRef, jacTravelHotels) => jacTravelHotels.find((h) => h.bookingReference === jacTravelRef);

const getHotelsInfo = (hotels = [], jacTravelHotels, isDotwBooking = false) => {
  const info = hotels
    .filter(({ cancelled }) => (hotels.length > 1 ? !cancelled : true))
    ?.map((hotel) => {
      const hotelInfo = {
        reference: hotel?.jacTravelRef ?? null,
        s3HotelPrice: Number(hotel?.price ?? 0) || null,
        s3CheckInDate: hotel?.checkInDate ?? null,
        s3GuestCount: hotel?.guestCount ?? null,
        s3NumberOfNights: hotel?.numberOfNights ?? null,
        jacTravelHotelPrice: null,
        jacTravelCheckInDate: null,
        jacTravelGuestCount: null,
        jacTravelNumberOfNights: null,
        itemRef: hotel?.itemRef
      };
      const jacTravelHotel = jacTravelHotels.find((h) => h.bookingReference === hotel.jacTravelRef);
      if (jacTravelHotel) {
        if (isDotwBooking) {
          hotelInfo.jacTravelHotelPrice = Number(jacTravelHotel?.priceBeforeMargin ?? 0) || null;
        } else {
          const totalPrice = Number(jacTravelHotel?.totalPrice ?? 0);
          const totalCommission = Number(jacTravelHotel?.totalCommission ?? 0);
          const beforeMargin = (isNaN(totalPrice) ? 0 : totalPrice) - (isNaN(totalCommission) ? 0 : totalCommission);
          hotelInfo.jacTravelHotelPrice = beforeMargin || null;
        }
        hotelInfo.jacTravelCheckInDate = jacTravelHotel?.arrivalDate ?? null;
        hotelInfo.jacTravelGuestCount = jacTravelHotel?.numberOfGuests ?? null;
        hotelInfo.jacTravelNumberOfNights = jacTravelHotel?.duration ?? null;
      }

      return hotelInfo;
    });

  const groupedHotels = groupBy(({ reference }) => reference, info);

  return flatten(Object.values(groupedHotels).map((arr) => arr[arr.length - 1]));
};
const nonCancelledEurostarAndGreenspeedLegs = (bookingLeg) =>
  bookingLeg?.filter(
    (leg) =>
      (!leg.cancelled && leg.serviceType?.code === serviceTypeCodes.EUROSTAR) ||
      (!leg.cancelled && leg.serviceType?.code === serviceTypeCodes.GREENSPEED)
  );

const nonCancelledLegs = (bookingLeg) => bookingLeg?.filter((leg) => !leg.cancelled);

const isConfirmed = (displayStatus) => ![PROVISIONAL, TEMPORARILY_ON_HOLD, ON_HOLD].includes(displayStatus);

const isSegmentOutbound = ({ direction } = {}) => direction === OUTBOUND;
const isSegmentInbound = ({ direction } = {}) => direction === INBOUND;

const isBookingMultiJourney = (details = []) => details?.some((detail) => detail?.key === MULTI_JOURNEY_KEY && detail?.value === 'true');

const isHotelOnly = (details = []) => details.some(({ key, value }) => key === 'product_type' && value === 'hotels');

const isBookingWithAOP = (booking = {}) =>
  (booking?.outbound?.some?.(({ products }) =>
    products.some(
      ({ afterSalesCurrentOverrides }) =>
        afterSalesCurrentOverrides?.length > 0 && afterSalesCurrentOverrides.some(({ type }) => type === 'REBOOK')
    )
  ) ??
    false) ||
  (booking?.inbound?.some?.(({ products }) =>
    products.some(
      ({ afterSalesCurrentOverrides }) =>
        afterSalesCurrentOverrides?.length > 0 && afterSalesCurrentOverrides.some(({ type }) => type === 'REBOOK')
    )
  ) ??
    false);

const hasPendingSocialMediaPayment = (booking) =>
  booking.payments.some((p) => p.paymentStatus === statusShortCode.pending && p.method === codes.SOCIAL_MEDIA);

const isSocialMediaPaymentPending = (booking, paymentLinkId) =>
  booking.payments.some(
    (p) => p.paymentStatus === statusShortCode.pending && p.method === codes.SOCIAL_MEDIA && p.externalReference === paymentLinkId
  );

const hasConflictedTicket = (tickets) => tickets?.some((ticket) => ticket.materialConflict && ticket.conflicts?.includes('MATERIAL'));

const isTicketConflicted = (ticket) => ticket?.materialConflict && ticket?.conflicts?.includes('MATERIAL');

const isEROnly = ({ outbound = [], inbound = [] }) => {
  const allLegs = [...outbound, ...inbound];
  return Boolean(allLegs.length) && allLegs.every((leg) => leg?.serviceType?.code === serviceTypeCodes.GREENSPEED);
};

const isESOrERSegment = (segment) =>
  segment?.serviceType?.code === serviceTypeCodes.EUROSTAR || segment?.serviceType?.code === serviceTypeCodes.GREENSPEED;

const isERSegment = (segment) => segment?.serviceType?.code === serviceTypeCodes.GREENSPEED;

const isPackage = ({ isPackage = false, hotels = [] }) => isPackage || Boolean(hotels.length);

/**
 * @typedef {Object} LegsByDirection
 * @property {Leg[]} outbound
 * @property {Leg[]} inbound
 */

/**
 * Sort legs by direction
 *
 * @param {Leg} [legs=[]]
 * @return {LegsByDirection}
 */
const sortLegsByDirection = (legs = []) => {
  const outbound = [];
  const inbound = [];

  for (const leg of legs) {
    if (isSegmentOutbound(leg)) {
      outbound.push(leg);
    } else {
      inbound.push(leg);
    }
  }

  return { outbound, inbound };
};

const isNewAriaBooking = (booking) => ARIA_AGENTS.includes(booking?.agent.office) && booking?.status === bookingStatus.PROVISIONAL;
/**
 * Sort pending creation legs by direction
 *
 * @param {Booking} booking
 * @param {Leg} [booking.pendingCreationLegs=[]]
 * @return {LegsByDirection}
 */
const sortPendingCreationLegsByDirection = ({ pendingCreationLegs = [] }) => sortLegsByDirection(pendingCreationLegs);

/**
 * Sort pending cancellation legs by direction
 *
 * @param {Booking} booking
 * @param {Leg} [booking.pendingCancellationLegs=[]]
 * @return {LegsByDirection}
 */
const sortPendingCancellationLegsByDirection = ({ pendingCancellationLegs = [] }) => sortLegsByDirection(pendingCancellationLegs);

const getPendingCancellationExtras = (extras = []) => extras.filter(({ cancelled, provisional }) => provisional && cancelled);

/**
 * @typedef {Object} ExtrasByDirection
 * @property {Products[]} outbound
 * @property {Products[]} inbound
 */

/**
 * Get pending cancellation extras
 *
 * @param {Booking} booking
 * @param {Product} [booking.outboundExtras=[]]
 * @param {Product} [booking.inboundExtras=[]]
 * @return {ExtrasByDirection}
 */
const getPendingCancellationExtrasByDirection = ({ outboundExtras = [], inboundExtras = [] }) => {
  const outbound = getPendingCancellationExtras(outboundExtras);
  const inbound = getPendingCancellationExtras(inboundExtras);
  return { outbound, inbound };
};

const hasDog = ({ passengers }) => passengers.some((passenger) => passenger.type === passengerTypes.DOG);

const hasTrainSegment = (booking) => Boolean(booking?.outbound?.length || booking?.inbound?.length);
const isExtrasOnlyBooking = (booking) =>
  !hasTrainSegment(booking) && Boolean(booking.outboundExtras?.length || booking.inboundExtras?.length);

const getBookingType = (booking) => {
  if (getAssistances([...(booking.outboundExtras || []), ...(booking.inboundExtras || [])]).length > 0) {
    return BOOKING_TYPES.ACCESSIBLE_TRAVEL;
  }

  if (booking.isGroupBooking) {
    return BOOKING_TYPES.GROUP;
  }

  if (isExtrasOnlyBooking(booking)) {
    return BOOKING_TYPES.EXTRAS_ONLY;
  }

  if (isHotelOnly(booking.additionalDetails)) {
    return BOOKING_TYPES.HOTEL_ONLY;
  }

  if (hasHotel(booking)) {
    return BOOKING_TYPES.PACKAGE;
  }

  return BOOKING_TYPES.GENERAL;
};

const hasPendingPayments = ({ payments }) => payments?.some(({ paymentStatus }) => paymentStatus === statusShortCode.pending);

const isABSOrADSSearch = (originUIC, destinationUIC) =>
  [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(originUIC) ||
  [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(destinationUIC);

const showNewFaresConditionsOnExchange = ({ products = [], isSameOrAfter = false }) =>
  products
    .filter(({ cancelled }) => !cancelled)
    ?.some(
      ({ productFamily, inventoryClass, confirmedTimestamp }) =>
        [productFamilyCodes.PUB, productFamilyCodes.RIT].includes(productFamily?.familyGroup) &&
        [B, TB, H, TH].includes(inventoryClass) &&
        !!confirmedTimestamp &&
        (isSameOrAfter
          ? moment(confirmedTimestamp).isSameOrAfter(moment(NEW_FARE_CONDITION_DATE_ON_EXCHANGE))
          : moment(confirmedTimestamp).isBefore(moment(NEW_FARE_CONDITION_DATE_ON_EXCHANGE)))
    );

const departureDateAfter7DaysFromNow = ({ departureDate }) => {
  const sevenDaysFromNow = moment().add(168, 'hours');
  return !!departureDate && moment(departureDate).isAfter(sevenDaysFromNow);
};
const showNCONonRefundableRules = ({
  products = [],
  withExchangedProducts = [],
  departureDate,
  activeCancelRule = [],
  bookingItemPrice,
  classOfServiceCode,
  serviceTypeCode
}) => {
  if (!EUROSTAR_CARRIERS.includes(serviceTypeCode)) return false;
  if (!SEVEN_DAY_EXCHANGE_RULE_CLASSES_OF_SERVICE.includes(classOfServiceCode)) return false;

  const passengersWithExchange = getPassengersWithExchangeInTicketChain(withExchangedProducts);
  const containsProductsAlreadyExchanged = products.some((product) => passengersWithExchange.has(product.passengerId));

  if (!containsProductsAlreadyExchanged) return false;
  if (activeCancelRule.length > 0 && bookingItemPrice) {
    const hasInValidCancelPrice = (rule, ticketPrice) => rule[0].price > ticketPrice;
    if (hasInValidCancelPrice(activeCancelRule, bookingItemPrice) || bookingItemPrice === 0) return false;
  }
  return (
    showNewFaresConditionsOnExchange({ products, isSameOrAfter: true }) &&
    departureDateAfter7DaysFromNow({ departureDate }) &&
    bookingItemPrice > 0
  );
};

const getSeniorsNumber = ({ containSNCBDBOrNSStation, seniors, seniors60To64, seniors65Plus }) => {
  if (containSNCBDBOrNSStation) {
    return seniors60To64 + seniors65Plus;
  }

  return seniors;
};

const buildSeniorsAges = ({ seniors60To64 = 0, seniors65Plus = 0 }) => {
  const seniorsTo64 = Number(seniors60To64);
  const seniorsAfter65 = Number(seniors65Plus);
  if (seniorsTo64 > 0 || seniorsAfter65 > 0) {
    const arrayOfAges = new Array(seniorsTo64 + seniorsAfter65);

    if (seniorsTo64 > 0) {
      arrayOfAges.fill(61, 0, seniorsTo64);
    }

    if (seniorsAfter65 > 0) {
      arrayOfAges.fill(66, seniorsTo64, seniorsTo64 + seniorsAfter65);
    }

    return arrayOfAges;
  }

  return [];
};

/**
 * Extracts date from extras-only booking additional details
 * @param {Array} additionalDetails - Array of additional detail objects
 * @param {string} direction - Direction ('outbound' or 'inbound')
 * @returns {string|null} Date string in format YYYY-MM-DD, or null if not found
 */
const getExtrasOnlyDate = (additionalDetails = [], direction = 'outbound') => {
  const key = direction === 'outbound' ? 'EXTRAS_ONLY_OUTBOUND' : 'EXTRAS_ONLY_INBOUND';
  const detail = additionalDetails.find((d) => d.key === key);

  if (!detail?.value) {
    return null;
  }

  // Value format: "trainNumber_YYYY-MM-DD"
  const parts = detail.value.split('_');
  if (parts.length === 2) {
    return parts[1]; // Return only the date
  }

  return null;
};

const isGDSBooking = ({ salesChannelCode = '' }) => salesChannelCode === GDS;

export {
  buildCustomerFromBillingDetails,
  isBalance,
  isPayout,
  hasPayout,
  hasParentPayment,
  getAssistances,
  getEvacAssistItemRefs,
  getValidExtras,
  findPayoutAndBalanceByRefId,
  hasHotel,
  isUserInEBSCOffice,
  isUserEurostarAgent,
  isUserStationAgent,
  isUserThalysAgent,
  getBookingAttachedLoyaltyAccount,
  buildBookingDataToSaveToCustomer,
  buildPointsRedemptionData,
  isOldBookingConfirmedTimestamp,
  isLoyaltyBooking,
  loyaltyPointsDifferencesCreationAndCancelLegs,
  calculateMilesUsed,
  getQuarter,
  hasBookingBeenCreatedInSameQuarter,
  getHotelsStatuses,
  getHotelsInfo,
  isConfirmed,
  isSegmentOutbound,
  isSegmentInbound,
  isBookingMultiJourney,
  isHotelOnly,
  isBookingWithAOP,
  isUserInCSCGroup,
  nonCancelledEurostarAndGreenspeedLegs,
  buildPointsRedemptionLoyaltyData,
  hasPendingSocialMediaPayment,
  isSocialMediaPaymentPending,
  isOverPaid,
  hasConflictedTicket,
  isTicketConflicted,
  convertGDSPayments,
  isEROnly,
  isPackage,
  sortPendingCancellationLegsByDirection,
  sortPendingCreationLegsByDirection,
  nonCancelledLegs,
  isNewAriaBooking,
  getPendingCancellationExtrasByDirection,
  hasDog,
  hasTrainSegment,
  isExtrasOnlyBooking,
  getBookingType,
  hasPendingPayments,
  findJacTravelHotel,
  isABSOrADSSearch,
  showNewFaresConditionsOnExchange,
  departureDateAfter7DaysFromNow,
  showNCONonRefundableRules,
  getSeniorsNumber,
  buildSeniorsAges,
  getExtrasOnlyDate,
  isESOrERSegment,
  isGDSBooking,
  isERSegment
};
